import { withStandardComponentWrapper } from 'helpers/HOC';
import { SvgIcon } from 'helpers/SvgIcon';
import { ComponentProps } from 'lib/component-props';
import { defaultConfig, tv } from 'tailwind-variants';
import MyChart from '../../../../helpers/MyChart/MyChart';
import useIsEditing from 'lib/hooks/useIsEditing';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import { AnswerProps, QuestionProps } from 'lib/hooks/open-scheduling-wizard/open-scheduling-types';
import { useWizardQuestionWidgetState } from 'lib/hooks/open-scheduling-wizard/useWizardQuestionWidgetState';
import { useEpicItemContext } from 'lib/hooks/open-scheduling-wizard/useEpicItemContext';
import { useSchedulingParamFilter } from 'lib/hooks/open-scheduling-wizard/useSchedulingParamFilter';
import { useRouter } from 'next/router';
import React from 'react';
defaultConfig.twMerge = false;

type OpenSchedulingWizardProps = ComponentProps & {
  fields?: {
    startQuestion?: QuestionProps;
  };
};

/* Open scheduling Wizard
Internal component so that it can be wrapped to provide a key for instance management.
 */
const SchedulingWizard = React.memo(function SchedulingWizard(props: OpenSchedulingWizardProps) {
  const isEditing = useIsEditing();

  // ToDo: Error handling if no startQuestion is included? While "optional", the component requires a question https://app.asana.com/1/9042223580080/project/1211036256662363/task/1211905214724518?focus=true
  const {
    currentQuestion,
    isFirstQuestion,
    visitTypeID,
    showIframe,
    handleAnswerClick,
    handleStartOver,
  } = useWizardQuestionWidgetState(props?.fields?.startQuestion);

  const { itemId, setItemName } = useAppointmentModal();
  const { epicID, typeOfItem } = useEpicItemContext(itemId, setItemName);
  const keyValueArray = useSchedulingParamFilter(props.params);

  const { base, heading, cardArrow } = TAILWIND_VARIANTS();

  return (
    <section className={base()}>
      <div className="w-full mx-auto max-w-screen-xxl px-10 lg:px-20 py-24">
        {showIframe && !isEditing ? (
          <MyChart
            vt={visitTypeID || ''}
            epicID={epicID || ''}
            typeOfItem={typeOfItem || ''}
            additionalParams={keyValueArray}
          />
        ) : (
          <>
            <h3 className={heading()}>
              <RichTextWrapper field={currentQuestion?.fields?.questionText} />
            </h3>
            <p className="text-16 leading-24 mb-12 text-gray-900">
              <RichTextWrapper field={currentQuestion?.fields?.additionalInfo} />
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {currentQuestion?.fields?.answers?.map((answer: AnswerProps, index: number) => (
                <button
                  key={`${answer?.id}-${index}`}
                  className={`group md:px-12 md:pb-12 p-12 bg-white rounded-md !w-full !h-auto shadow-default hover:shadow-hover flex justify-between items-left text-left ${answer.fields?.answerHelp?.value !== '' || isEditing ? 'flex-col gap-0' : 'flex-row gap-6'}`}
                  onClick={() => handleAnswerClick(answer)}
                >
                  <h4
                    className={`font-bold font-centuryGothic ${answer.fields?.answerHelp?.value !== '' ? 'mb-8 md:text-22 text-20 md:leading-31 leading-28' : 'text-22 leading-31'}`}
                  >
                    <RichTextWrapper field={answer.fields?.answerText} />
                  </h4>
                  <p className="text-16 leading-24 mb-6">
                    <RichTextWrapper field={answer.fields?.answerHelp} />
                  </p>

                  <div className="flex justify-end">
                    <span className={cardArrow()}>
                      <SvgIcon
                        icon="arrow-right"
                        size="sm"
                        className={`duration-700 ease-in-out absolute text-blue-base left-[30%] group-hover:left-[60%]`}
                      />
                      <SvgIcon
                        icon="arrow-right"
                        size="sm"
                        className={`duration-700 ease-in-out absolute text-white -left-[30%] group-hover:left-[30%]`}
                      />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
        {!isFirstQuestion && !showIframe && (
          <ButtonWrapper
            text="Start Over"
            onClick={handleStartOver}
            className="w-full md:w-auto mt-[40px]"
            ctaVariant="secondary"
          />
        )}
      </div>
    </section>
  );
});

const OpenSchedulingWizard = (props: OpenSchedulingWizardProps) => {
  const router = useRouter();
  const key = `${router.asPath}-${props.fields?.startQuestion?.id || 'defaultKey'}`;
  return <SchedulingWizard {...props} key={key} />;
};

export const Default = withStandardComponentWrapper(OpenSchedulingWizard);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['component bg-white w-full theme-primary'],
    heading: ['md:text-26 text-24 font-bold font-centuryGothic md:leading-30 leading-[29px] mb-16'],
    cardArrow:
      'md:w-[48px] md:h-[48px] w-[40px] h-[40px] rounded-full border-2 text-center border-blue-base flex items-center justify-center transition-all duration-300 ease-in-out overflow-hidden duration-700 relative group-hover:bg-blue-base',
  },
});
